<template>
    <div class="sales-personal-stats">
        <!-- Мотивационный блок (можно скрыть через prop) -->
        <div v-if="!hideMotivationText" class="motivation-header mb-4">
            <div class="card">
                <div class="card-body">
                    <h5 class="card-title">Система мотивации отдела продаж</h5>
                    <p class="card-text">
                        Бонус = 10% от сверхокупаемости (маржа − затраты компании). Чем больше чистой прибыли — тем больше ваш доход.
                    </p>
                    <ul class="mb-0">
                        <li>Фокус на сделках с положительной маржой</li>
                        <li>Увеличивайте входящие платежи и оптимизируйте закупки</li>
                        <li>Держите контроль над закрытием сделок и дебиторкой</li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="card mb-3">
            <div class="card-header">
                <h6 class="mb-0">Персональная мотивация (продажи)</h6>
            </div>
            <div class="card-body">
                <div v-if="loading" class="text-center py-3">
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">Загрузка...</span>
                    </div>
                </div>
                <div v-else>
                    <div class="row">
                        <div class="col-md-4">
                            <div class="calculation-step text-center">
                                <h6>Шаг 1: Маржинальность</h6>
                                <p class="text-muted">Сумма прибыли от вашей работы</p>
                                <div class="calculation-result">
                                    <span class="h4 text-success font-weight-bold">{{ formatCurrency(stats.margin) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="calculation-step text-center">
                                <h6>Шаг 2: Сверхокупаемость</h6>
                                <p class="text-muted">Маржа − ЗП (и прочие затраты компании)</p>
                                <div class="calculation-result">
                                    <span class="h4 text-info font-weight-bold">{{ formatCurrency(stats.superIncome) }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4">
                            <div class="calculation-step text-center">
                                <h6>Шаг 3: Рассчитанный бонус</h6>
                                <p class="text-muted">10% от сверхокупаемости</p>
                                <div class="calculation-result">
                                    <span class="h4 text-primary font-weight-bold">{{ formatCurrency(stats.bonus) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Детальные расчеты маржинальности -->
        <div class="marginality-calculation-table mb-4">
            <div class="card">
                <div class="card-header">
                    <h6 class="mb-0">Детализация маржинальности</h6>
                </div>
                <div class="card-body">
                    <div v-if="detailedLoading" class="text-center py-3">
                        <div class="spinner-border spinner-border-sm text-primary" role="status">
                            <span class="sr-only">Загрузка...</span>
                        </div>
                        <p class="mt-2 mb-0 text-muted">Загрузка детальных расчетов...</p>
                    </div>
                    <div v-else-if="detailedItems.length > 0">
                        <b-table
                            :items="detailedItems"
                            :fields="detailedFields"
                            small
                            striped
                            hover
                            responsive
                            class="mb-0"
                        >
                            <template #cell(order_id)="row">
                                <span 
                                    class="sidebar-link text-primary font-weight-bold cursor-pointer" 
                                    @click="openInNewWindow ? openOrderInNewWindow(row.item.order_id) : openOrderSidebar(row.item.order_id, '')" 
                                    :title="openInNewWindow ? 'Открыть заказ в новой вкладке' : 'Открыть заказ'"
                                >
                                    #{{ row.item.order_id }}
                                </span>
                            </template>
                            <template #cell(incoming_total)="row">
                                <span class="text-secondary font-weight-bold">{{ formatCurrency(row.item.incoming_total) }}</span>
                            </template>
                            <template #cell(outgoing_total)="row">
                                <span class="text-primary font-weight-bold">{{ formatCurrency(row.item.outgoing_total) }}</span>
                            </template>
                            <template #cell(margin)="row">
                                <span :class="row.item.is_positive_margin ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                    {{ formatCurrency(row.item.margin) }}
                                </span>
                            </template>
                            <template #cell(is_positive_margin)="row">
                                <span class="badge" :class="row.item.is_positive_margin ? 'badge-success' : (Number(row.item.margin) === 0 ? 'badge-secondary' : 'badge-danger')">
                                    {{ row.item.is_positive_margin ? 'Прибыль' : (Number(row.item.margin) === 0 ? 'Нулевая маржа' : 'Убыток') }}
                                </span>
                            </template>
                            <template #cell(calculated_at)="row">
                                <small class="text-muted">{{ formatDate(row.item.calculated_at) }}</small>
                            </template>
                        </b-table>
                    </div>
                    <div v-else class="text-center py-4">
                        <i class="fas fa-chart-line fa-2x text-muted mb-2"></i>
                        <p class="text-muted mb-0">Нет данных о детальных расчетах за выбранный период</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- График маржинальности (персональный) -->
        <div class="charts-section mb-4">
            <div class="card bdr-tabs">
                <div class="card-header">
                    <h6 class="mb-0">Маржинальность по месяцам</h6>
                </div>
                <div class="card-body">
                    <MotivationMarginalityChart 
                        :key="`sales-personal-marginality-${chartKey}`"
                        :rawChartData="marginalityChartData"
                        :loading="marginalityChartLoading"
                        title="Маржинальность (₽)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { API_MOTIVATION2025_SALES, API_MOTIVATION2025_SALES_DETAILED_CALCULATIONS, API_MOTIVATION2025_CHARTS_SALES_MARGINALITY } from '@/constants/api'
import MotivationMarginalityChart from '@components/charts/MotivationMarginalityChart.vue'
import { openOrderSidebar } from '@/mixins/openOrderSidebar'

export default {
    name: 'SalesPersonalStats',
    components: { MotivationMarginalityChart },
    mixins: [openOrderSidebar],
    props: {
        userId: { type: Number, required: true },
        period: { type: String, default: 'current' },
        departmentId: { type: Number, default: 1 },
        hideMotivationText: { type: Boolean, default: false },
        openInNewWindow: { type: Boolean, default: false }
    },
    data() {
        return {
            loading: false,
            stats: { margin: 0, superIncome: 0, bonus: 0 },
            currentPeriod: '',
            detailedItems: [],
            detailedLoading: false,
            detailedFields: [
                { key: 'order_id', label: 'Заказ', sortable: true, thClass: 'text-left', tdClass: 'text-left' },
                { key: 'incoming_total', label: 'Входящие платежи', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
                { key: 'outgoing_total', label: 'Исходящие платежи', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
                { key: 'margin', label: 'Маржа', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
                { key: 'is_positive_margin', label: 'Статус', sortable: true, thClass: 'text-center', tdClass: 'text-center' },
                { key: 'calculated_at', label: 'Дата расчета', sortable: true, thClass: 'text-left', tdClass: 'text-left' },
            ],
            marginalityChartLoading: false,
            marginalityChartData: [],
            chartKey: 0
        }
    },
    mounted() {
        this.currentPeriod = this.period === 'current' ? this.getCurrentQuarter() : this.period
        this.load()
    },
    methods: {
        load() {
            this.loading = true
            const params = { quarter: this.currentPeriod, department_id: this.departmentId, user_id: this.userId }
            axios.get(API_MOTIVATION2025_SALES, { params })
                .then(res => {
                    if (res.data.success) {
                        const data = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data
                        if (data) {
                            this.stats = {
                                margin: data.margin?.total_margin || 0,
                                superIncome: data.super_income || 0,
                                bonus: data.bonus || 0
                            }
                        }
                    }
                })
                .finally(() => this.loading = false)

            // Детализация
            this.loadDetailedCalculations()
            // График маржинальности по сотруднику
            this.loadMarginalityChartData()
        },
        loadMarginalityChartData() {
            this.marginalityChartLoading = true
            const params = { quarter: this.currentPeriod, department_id: this.departmentId, user_id: this.userId }
            axios.get(API_MOTIVATION2025_CHARTS_SALES_MARGINALITY, { params })
                .then(res => {
                    if (res.data.success) {
                        this.marginalityChartData = res.data.data
                        this.$nextTick(() => { this.chartKey++ })
                    }
                })
                .catch(err => console.error('Error loading sales marginality chart (personal):', err))
                .finally(() => { this.marginalityChartLoading = false })
        },
        loadDetailedCalculations() {
            this.detailedLoading = true
            axios.get(API_MOTIVATION2025_SALES_DETAILED_CALCULATIONS, {
                params: { quarter: this.currentPeriod, user_id: this.userId }
            })
            .then(res => {
                if (res.data.success) {
                    this.detailedItems = res.data.data || []
                } else {
                    this.detailedItems = []
                }
            })
            .catch(() => { this.detailedItems = [] })
            .finally(() => { this.detailedLoading = false })
        },
        formatCurrency(amount) {
            return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 0 }).format(amount || 0)
        },
        getCurrentQuarter() {
            const now = new Date()
            const year = now.getFullYear()
            const month = now.getMonth() + 1
            const minYear = 2025
            const minQuarter = 4 // Q4 2025 - минимальный квартал
            
            let currentQuarter = ''
            
            // Финансовая схема кварталов (год начинается с декабря):
            // Q1: декабрь, январь, февраль (12,1,2)
            // Q2: март, апрель, май (3,4,5)
            // Q3: июнь, июль, август (6,7,8)
            // Q4: сентябрь, октябрь, ноябрь (9,10,11)
            if (month === 12 || month === 1 || month === 2) {
                currentQuarter = month === 12 ? `${year + 1}-Q1` : `${year}-Q1`
            } else if (month >= 3 && month <= 5) {
                currentQuarter = `${year}-Q2`
            } else if (month >= 6 && month <= 8) {
                currentQuarter = `${year}-Q3`
            } else if (month >= 9 && month <= 11) {
                currentQuarter = `${year}-Q4`
            }
            
            // Проверяем, что квартал не раньше Q4 2025
            const [qYear, qNum] = currentQuarter.split('-Q')
            const quarterNum = parseInt(qNum)
            
            if (qYear < minYear || (qYear == minYear && quarterNum < minQuarter)) {
                return '2025-Q4' // Возвращаем минимальный доступный квартал
            }
            
            return currentQuarter || '2025-Q4'
        },
        formatDate(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        },
        openOrderInNewWindow(orderId) {
            const routeData = this.$router.resolve({ path: '/orders', query: { order: orderId } })
            window.open(routeData.href, '_blank')
        }
    }
}
</script>

<style scoped>
.h5 { font-weight: 700; }
.sidebar-link { cursor: pointer; text-decoration: underline; transition: all 0.1s ease; }
.sidebar-link:hover { color: #0056b3 !important; text-decoration: none; font-weight: bold; }
.marginality-calculation-table .table th { background-color: #f8f9fa; border-top: none; font-weight: 600; color: #495057; }
</style>


